/**
 * Open AP Bills with Discounts Report
 * 
 * Shows all open AP bills with early-pay discount information.
 * 
 * Queries:
 *   1. APBILL (always) - bill headers with term/discount dates
 *   2. APBILLITEM (always) - line-item amounts for open balance + discount base calc
 *   3. TERM (always) - discount rules (%, $, calc-on logic)
 * 
 * Discount logic:
 *   - Eligibility: today <= APBILL.WHENDISCOUNT
 *   - TERM.DISCCALCON determines the discount base:
 *     "Bill total"       → sum of ALL line TRX_AMOUNT
 *     "Line items total" → sum of line TRX_AMOUNT WHERE SUBTOTAL != 'S'
 *   - TERM.DISCPERCAMN: '%' = percentage, '$' = flat amount
 *   - Discount is calculated on TRX_AMOUNT (original), not on open balance
 *   - When not eligible, discount columns are zeroed out
 * 
 * Row grouping: Level 0 = bill summary, Level 1 = line item detail
 * 
 * Excluded APBILL states: Reversed, Reversal, Voided, Deleted, Paid
 * Additional filter: open amount (TRX_AMOUNT - TRX_TOTALPAID) > 0
 */

import { parseDate } from './reportHelpers';

const EXCLUDED_STATES = ['Reversed', 'Reversal', 'Voided', 'Deleted', 'Paid'];

const openApBillsDiscount = {
  id: 'open-ap-bills-discount',
  name: 'Open AP Bills with Discounts',
  description: 'Open AP bills with early-pay discount eligibility, discount calculation, and after-discount totals. Expandable line item detail shows which lines contribute to the discount base.',
  category: 'Accounts Payable',

  parameters: [
    {
      id: 'vendorFilter', label: 'Vendor ID Filter', type: 'text',
      default: '', description: 'Optional: filter to a specific VENDORID (leave blank for all)'
    },
  ],

  queries: [
    {
      id: 'bills', object: 'APBILL',
      fields: [
        'RECORDNO', 'RECORDID', 'DOCNUMBER', 'VENDORID', 'VENDORNAME',
        'WHENCREATED', 'WHENDUE', 'WHENDISCOUNT',
        'TOTALDUE', 'TOTALENTERED', 'TOTALPAID',
        'STATE', 'DESCRIPTION', 'CURRENCY',
        'TERMNAME', 'TERMKEY', 'DUE_IN_DAYS',
        'MEGAENTITYID', 'RECORD_URL'
      ],
      buildFilter: (params) => {
        const parts = ["STATE NOT IN ('Reversed','Reversal','Voided','Deleted','Paid')"];
        if (params.vendorFilter?.trim()) {
          parts.push("VENDORID = '" + params.vendorFilter.trim() + "'");
        }
        return parts.join(' AND ');
      }
    },
    {
      id: 'lineItems', object: 'APBILLITEM',
      fields: [
        'APBILL.RECORDNO', 'APBILL.RECORDID',
        'LINE_NO', 'ACCOUNTNO', 'ACCOUNTTITLE', 'ENTRYDESCRIPTION',
        'TRX_AMOUNT', 'TRX_TOTALPAID', 'SUBTOTAL',
        'LOCATIONID', 'DEPARTMENTID', 'PROJECTID', 'ITEMID', 'ITEMNAME'
      ],
      buildFilter: (params) => {
        const parts = ["STATE NOT IN ('Reversed','Reversal','Voided','Deleted')"];
        if (params.vendorFilter?.trim()) {
          parts.push("VENDORID = '" + params.vendorFilter.trim() + "'");
        }
        return parts.join(' AND ');
      }
    },
    {
      id: 'terms', object: 'APTERM',
      fields: [
        'RECORDNO', 'NAME', 'DESCRIPTION',
        'DISCAMOUNT', 'DISCCALCON', 'DISCDATE', 'DISCFROM', 'DISCPERCAMN'
      ],
      buildFilter: () => "STATUS = 'active'"
    }
  ],

  additionalQueries: [],
  optionalColumns: [],

  process: (datasets, params) => {
    const bills = datasets.bills || [];
    const lineItems = datasets.lineItems || [];
    const terms = datasets.terms || [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ── Term lookup: RECORDNO → term object ──
    const termLookup = {};
    for (const t of terms) {
      const rn = String(t.RECORDNO || '').trim();
      if (rn) termLookup[rn] = t;
    }

    // ── Group line items by bill RECORDNO ──
    const linesByBill = {};
    for (const line of lineItems) {
      const billRn = String(line['APBILL.RECORDNO'] || '').trim();
      if (!billRn) continue;
      if (!linesByBill[billRn]) linesByBill[billRn] = [];
      linesByBill[billRn].push(line);
    }

    // ── Build report rows ──
    const data = [];
    const rowOutlineLevels = [];
    const detailRowIndices = [];

    let grandBillAmount = 0;
    let grandPaid = 0;
    let grandOpenAmount = 0;
    let grandDiscountAmount = 0;
    let grandAfterDiscount = 0;
    let skippedState = 0;
    let skippedZero = 0;

    // Sort bills by vendor, then bill ID
    const sortedBills = [...bills].sort((a, b) => {
      const vc = (a.VENDORID || '').localeCompare(b.VENDORID || '');
      return vc !== 0 ? vc : (a.RECORDID || '').localeCompare(b.RECORDID || '');
    });

    for (const bill of sortedBills) {
      const state = String(bill.STATE || '').trim();
      if (EXCLUDED_STATES.includes(state)) { skippedState++; continue; }

      const rn = String(bill.RECORDNO || '').trim();
      const lines = linesByBill[rn] || [];

      // ── Aggregate line-level amounts ──
      let totalBillAmount = 0;
      let totalPaid = 0;
      let lineItemsOnlyBase = 0;  // sum of non-S lines

      for (const line of lines) {
        const amt = parseFloat(line.TRX_AMOUNT) || 0;
        const paid = parseFloat(line.TRX_TOTALPAID) || 0;
        const subtotal = String(line.SUBTOTAL || '').trim();

        totalBillAmount += amt;
        totalPaid += paid;

        if (subtotal !== 'S') {
          lineItemsOnlyBase += amt;
        }
      }

      totalBillAmount = Math.round(totalBillAmount * 100) / 100;
      totalPaid = Math.round(totalPaid * 100) / 100;
      lineItemsOnlyBase = Math.round(lineItemsOnlyBase * 100) / 100;

      const openAmount = Math.round((totalBillAmount - totalPaid) * 100) / 100;

      // Skip bills with no open balance
      if (openAmount <= 0) { skippedZero++; continue; }

      // ── Term + discount logic ──
      const termKey = String(bill.TERMKEY || '').trim();
      const term = termLookup[termKey];

      const discountDate = parseDate(bill.WHENDISCOUNT);
      const isEligible = discountDate && today <= discountDate;

      // Determine discount base
      const discCalcOn = term ? String(term.DISCCALCON || '').trim() : '';
      let discountBase = 0;
      if (discCalcOn === 'Bill total') {
        discountBase = totalBillAmount;
      } else if (discCalcOn === 'Line items total') {
        discountBase = lineItemsOnlyBase;
      } else {
        // Fallback (no term or unknown value) — use bill total
        discountBase = totalBillAmount;
      }

      // Calculate discount amount (only if eligible)
      let discountAmount = 0;
      let discountDisplay = '';
      if (term) {
        const discAmtVal = parseFloat(term.DISCAMOUNT) || 0;
        const discType = String(term.DISCPERCAMN || '').trim();

        if (discType === '%') {
          discountDisplay = discAmtVal + '%';
          if (isEligible) {
            discountAmount = Math.round(discountBase * discAmtVal / 100 * 100) / 100;
          }
        } else {
          discountDisplay = '$' + discAmtVal.toFixed(2);
          if (isEligible) {
            discountAmount = Math.round(discAmtVal * 100) / 100;
          }
        }
      }

      const afterDiscount = Math.round((openAmount - discountAmount) * 100) / 100;

      // Grand totals
      grandBillAmount += totalBillAmount;
      grandPaid += totalPaid;
      grandOpenAmount += openAmount;
      grandDiscountAmount += discountAmount;
      grandAfterDiscount += afterDiscount;

      // ── Summary row (level 0) ──
      const summaryRow = {
        'Vendor ID': bill.VENDORID || '',
        'Vendor Name': bill.VENDORNAME || '',
        'Bill Number': bill.RECORDID || '',
        'Doc Number': bill.DOCNUMBER || '',
        'Description': bill.DESCRIPTION || '',
        'Bill Date': bill.WHENCREATED || '',
        'Due Date': bill.WHENDUE || '',
        'Term': bill.TERMNAME || '',
        'Discount Date': bill.WHENDISCOUNT || '',
        'Discount Eligible': isEligible ? 'Yes' : 'No',
        'Discount Calc On': discCalcOn || '',
        'Discount Rate': discountDisplay,
        'Bill Amount': totalBillAmount,
        'Amount Paid': totalPaid,
        'Open Amount': openAmount,
        'Discount Base': isEligible ? discountBase : 0,
        'Discount Amount': discountAmount,
        'After Discount': afterDiscount,
        // Detail-only columns (blank on summary)
        'Line #': '',
        'Account No': '',
        'Account Title': '',
        'Subtotal Flag': '',
        // Hidden metadata
        '_recordUrl': bill.RECORD_URL || '',
        '_entity': bill.MEGAENTITYID || '',
      };

      data.push(summaryRow);
      rowOutlineLevels.push(0);

      // ── Detail rows (level 1) — one per line item ──
      const sortedLines = [...lines].sort((a, b) => {
        return (parseInt(a.LINE_NO) || 0) - (parseInt(b.LINE_NO) || 0);
      });

      for (const line of sortedLines) {
        const lineAmt = parseFloat(line.TRX_AMOUNT) || 0;
        const linePaid = parseFloat(line.TRX_TOTALPAID) || 0;
        const lineOpen = Math.round((lineAmt - linePaid) * 100) / 100;
        const subtotal = String(line.SUBTOTAL || '').trim();

        // Show if this line is excluded from discount calc
        let lineDiscNote = '';
        if (subtotal === 'S' && discCalcOn === 'Line items total') {
          lineDiscNote = 'Excluded (S)';
        }

        const detailRow = {
          'Vendor ID': '',
          'Vendor Name': '',
          'Bill Number': '',
          'Doc Number': '',
          'Description': line.ENTRYDESCRIPTION || '',
          'Bill Date': '',
          'Due Date': '',
          'Term': '',
          'Discount Date': '',
          'Discount Eligible': lineDiscNote,
          'Discount Calc On': '',
          'Discount Rate': '',
          'Bill Amount': lineAmt,
          'Amount Paid': linePaid,
          'Open Amount': lineOpen,
          'Discount Base': '',
          'Discount Amount': '',
          'After Discount': '',
          'Line #': line.LINE_NO || '',
          'Account No': line.ACCOUNTNO || '',
          'Account Title': line.ACCOUNTTITLE || '',
          'Subtotal Flag': subtotal,
        };

        detailRowIndices.push(data.length);
        data.push(detailRow);
        rowOutlineLevels.push(1);
      }
    }

    // ── Columns ──
    const columns = [
      'Vendor ID', 'Vendor Name', 'Bill Number', 'Doc Number', 'Description',
      'Bill Date', 'Due Date', 'Term',
      'Discount Date', 'Discount Eligible', 'Discount Calc On', 'Discount Rate',
      'Bill Amount', 'Amount Paid', 'Open Amount',
      'Discount Base', 'Discount Amount', 'After Discount',
      'Line #', 'Account No', 'Account Title', 'Subtotal Flag'
    ];

    // Group the line-detail columns (hidden by default, expand to see)
    const lineDetailStart = columns.indexOf('Line #');
    const groupedColumns = [{
      indices: [lineDetailStart, lineDetailStart + 1, lineDetailStart + 2, lineDetailStart + 3]
    }];

    // ── Summary stats ──
    const billCount = data.filter((_, i) => rowOutlineLevels[i] === 0).length;
    const vendorCount = new Set(
      data.filter((_, i) => rowOutlineLevels[i] === 0)
        .map(r => r['Vendor ID'])
        .filter(Boolean)
    ).size;
    const eligibleCount = data.filter(
      (r, i) => rowOutlineLevels[i] === 0 && r['Discount Eligible'] === 'Yes'
    ).length;

    const summaryLines = [
      billCount + ' open bill(s) across ' + vendorCount + ' vendor(s)',
      eligibleCount + ' bill(s) eligible for early-pay discount',
      '',
      'Total Bill Amount: $' + grandBillAmount.toLocaleString('en-US', { minimumFractionDigits: 2 }),
      'Total Paid: $' + grandPaid.toLocaleString('en-US', { minimumFractionDigits: 2 }),
      'Total Open: $' + grandOpenAmount.toLocaleString('en-US', { minimumFractionDigits: 2 }),
      'Total Discount Available: $' + grandDiscountAmount.toLocaleString('en-US', { minimumFractionDigits: 2 }),
      'Total After Discount: $' + grandAfterDiscount.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    ];
    if (skippedZero > 0) summaryLines.push('', '(' + skippedZero + ' zero-balance bill(s) excluded)');
    if (skippedState > 0) summaryLines.push('(' + skippedState + ' reversed/voided/paid bill(s) excluded)');

    return {
      columns,
      data,
      detailRowIndices,
      rowOutlineLevels,
      groupedColumns,
      formatRules: {
        'Bill Amount': { type: 'currency', decimals: 2 },
        'Amount Paid': { type: 'currency', decimals: 2 },
        'Open Amount': { type: 'currency', decimals: 2 },
        'Discount Base': { type: 'currency', decimals: 2 },
        'Discount Amount': { type: 'currency', decimals: 2 },
        'After Discount': { type: 'currency', decimals: 2 },
        'Bill Number': { type: 'hyperlink', urlKey: '_recordUrl', entityKey: '_entity' },
      },
      summary: { lines: summaryLines },
    };
  },

  formatRules: {
    'Bill Amount': { type: 'currency', decimals: 2 },
    'Amount Paid': { type: 'currency', decimals: 2 },
    'Open Amount': { type: 'currency', decimals: 2 },
    'Discount Base': { type: 'currency', decimals: 2 },
    'Discount Amount': { type: 'currency', decimals: 2 },
    'After Discount': { type: 'currency', decimals: 2 },
    'Bill Number': { type: 'hyperlink', urlKey: '_recordUrl', entityKey: '_entity' },
  }
};

export default openApBillsDiscount;
